import React, { useState, useEffect } from 'react';
import {
    Edit,
    SimpleForm,
    TextInput,
    required,
    minLength,
    regex,
    useTranslate,
    useRedirect,
    useNotify,
    SaveButton,
    DeleteButton,
    Toolbar,
    useRecordContext,
    DateField,
    useDataProvider,
    useInput,
} from 'react-admin';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

const ROLE_KNOWN_FIELDS = new Set([
    'name', 'description', 'permissions', 'isActive',
]);

const mapRoleBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const roleFieldLabelKey = (field: string): string =>
    ROLE_KNOWN_FIELDS.has(field)
        ? `resources.roles.fields.${field}`
        : field;
import { 
    Box, 
    Card, 
    CardContent, 
    Typography, 
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    FormControlLabel,
    Checkbox,
    Chip,
    Alert,
    Divider,
} from '@mui/material';
import {
    ArrowBack,
    Security,
    Description,
    ExpandMore,
    People,
    PersonRemove,
    LocationOn,
    EvStation,
    Receipt,
    CalendarToday,
    Settings,
    Assessment,
    Bolt,
    AccountBalanceWallet,
    Info,
    Notifications,
    AlternateEmail,
    Storage,
    Timeline,
    CloudQueue,
    Campaign,
    LocalOffer,
    SystemUpdate,
    CloudDownload,
    AttachMoney,
    DirectionsCar,
    Group,
    Policy,
    Psychology,
    Storefront,
    VpnKey,
} from '@mui/icons-material';

// Use translation-based permission groups
const getPermissionGroups = (translate: any) => ({
    users: {
        label: translate('resources.roles.permissionGroups.users.label'),
        icon: <People />,
        color: '#1976D2',
        permissions: [
            { id: 'users:read:all', label: translate('resources.roles.permissionGroups.users.permissions.users:read:all.label'), description: translate('resources.roles.permissionGroups.users.permissions.users:read:all.description') },
            { id: 'users:create:all', label: translate('resources.roles.permissionGroups.users.permissions.users:create:all.label'), description: translate('resources.roles.permissionGroups.users.permissions.users:create:all.description') },
            { id: 'users:update:all', label: translate('resources.roles.permissionGroups.users.permissions.users:update:all.label'), description: translate('resources.roles.permissionGroups.users.permissions.users:update:all.description') },
            { id: 'users:admin:all', label: translate('resources.roles.permissionGroups.users.permissions.users:admin:all.label'), description: translate('resources.roles.permissionGroups.users.permissions.users:admin:all.description') },
        ]
    },
    roles: {
        label: translate('resources.roles.permissionGroups.roles.label'),
        icon: <Security />,
        color: '#009A75',
        permissions: [
            { id: 'roles:read:all', label: translate('resources.roles.permissionGroups.roles.permissions.roles:read:all.label'), description: translate('resources.roles.permissionGroups.roles.permissions.roles:read:all.description') },
            { id: 'roles:write:all', label: translate('resources.roles.permissionGroups.roles.permissions.roles:write:all.label'), description: translate('resources.roles.permissionGroups.roles.permissions.roles:write:all.description') },
            { id: 'roles:admin:all', label: translate('resources.roles.permissionGroups.roles.permissions.roles:admin:all.label'), description: translate('resources.roles.permissionGroups.roles.permissions.roles:admin:all.description') },
        ]
    },
    sites: {
        label: translate('resources.roles.permissionGroups.sites.label'),
        icon: <LocationOn />,
        color: '#FF6B35',
        permissions: [
            { id: 'sites:read:all', label: translate('resources.roles.permissionGroups.sites.permissions.sites:read:all.label'), description: translate('resources.roles.permissionGroups.sites.permissions.sites:read:all.description') },
            { id: 'sites:write:all', label: translate('resources.roles.permissionGroups.sites.permissions.sites:write:all.label'), description: translate('resources.roles.permissionGroups.sites.permissions.sites:write:all.description') },
            { id: 'sites:admin:all', label: translate('resources.roles.permissionGroups.sites.permissions.sites:admin:all.label'), description: translate('resources.roles.permissionGroups.sites.permissions.sites:admin:all.description') },
        ]
    },
    'load-groups': {
        label: translate('resources.roles.permissionGroups.load-groups.label'),
        icon: <Bolt />,
        color: '#7B68EE',
        permissions: [
            { id: 'load-groups:read:all', label: translate('resources.roles.permissionGroups.load-groups.permissions.load-groups:read:all.label'), description: translate('resources.roles.permissionGroups.load-groups.permissions.load-groups:read:all.description') },
            { id: 'load-groups:write:all', label: translate('resources.roles.permissionGroups.load-groups.permissions.load-groups:write:all.label'), description: translate('resources.roles.permissionGroups.load-groups.permissions.load-groups:write:all.description') },
            { id: 'load-groups:admin:all', label: translate('resources.roles.permissionGroups.load-groups.permissions.load-groups:admin:all.label'), description: translate('resources.roles.permissionGroups.load-groups.permissions.load-groups:admin:all.description') },
        ]
    },
    chargers: {
        label: translate('resources.roles.permissionGroups.chargers.label'),
        icon: <EvStation />,
        color: '#009A75',
        permissions: [
            { id: 'chargers:read:all', label: translate('resources.roles.permissionGroups.chargers.permissions.chargers:read:all.label'), description: translate('resources.roles.permissionGroups.chargers.permissions.chargers:read:all.description') },
            { id: 'chargers:write:all', label: translate('resources.roles.permissionGroups.chargers.permissions.chargers:write:all.label'), description: translate('resources.roles.permissionGroups.chargers.permissions.chargers:write:all.description') },
            { id: 'chargers:admin:all', label: translate('resources.roles.permissionGroups.chargers.permissions.chargers:admin:all.label'), description: translate('resources.roles.permissionGroups.chargers.permissions.chargers:admin:all.description') },
            { id: 'chargers:reboot', label: translate('resources.roles.permissionGroups.chargers.permissions.chargers:reboot.label'), description: translate('resources.roles.permissionGroups.chargers.permissions.chargers:reboot.description') },
        ]
    },
    'charger-types': {
        label: translate('resources.roles.permissionGroups.charger-types.label'),
        icon: <Bolt />,
        color: '#9C27B0',
        permissions: [
            { id: 'charger-types:read:all', label: translate('resources.roles.permissionGroups.charger-types.permissions.charger-types:read:all.label'), description: translate('resources.roles.permissionGroups.charger-types.permissions.charger-types:read:all.description') },
            { id: 'charger-types:write:all', label: translate('resources.roles.permissionGroups.charger-types.permissions.charger-types:write:all.label'), description: translate('resources.roles.permissionGroups.charger-types.permissions.charger-types:write:all.description') },
            { id: 'charger-types:admin:all', label: translate('resources.roles.permissionGroups.charger-types.permissions.charger-types:admin:all.label'), description: translate('resources.roles.permissionGroups.charger-types.permissions.charger-types:admin:all.description') },
        ]
    },
    transactions: {
        label: translate('resources.roles.permissionGroups.transactions.label'),
        icon: <Receipt />,
        color: '#FF9800',
        permissions: [
            { id: 'transactions:read:all', label: translate('resources.roles.permissionGroups.transactions.permissions.transactions:read:all.label'), description: translate('resources.roles.permissionGroups.transactions.permissions.transactions:read:all.description') },
            { id: 'transactions:write:all', label: translate('resources.roles.permissionGroups.transactions.permissions.transactions:write:all.label'), description: translate('resources.roles.permissionGroups.transactions.permissions.transactions:write:all.description') },
            { id: 'transactions:admin:all', label: translate('resources.roles.permissionGroups.transactions.permissions.transactions:admin:all.label'), description: translate('resources.roles.permissionGroups.transactions.permissions.transactions:admin:all.description') },
        ]
    },
    reservations: {
        label: translate('resources.roles.permissionGroups.reservations.label'),
        icon: <CalendarToday />,
        color: '#3F51B5',
        permissions: [
            { id: 'reservations:read:all', label: translate('resources.roles.permissionGroups.reservations.permissions.reservations:read:all.label'), description: translate('resources.roles.permissionGroups.reservations.permissions.reservations:read:all.description') },
            { id: 'reservations:write:all', label: translate('resources.roles.permissionGroups.reservations.permissions.reservations:write:all.label'), description: translate('resources.roles.permissionGroups.reservations.permissions.reservations:write:all.description') },
            { id: 'reservations:admin:all', label: translate('resources.roles.permissionGroups.reservations.permissions.reservations:admin:all.label'), description: translate('resources.roles.permissionGroups.reservations.permissions.reservations:admin:all.description') },
        ]
    },
    'reservation-policies': {
        label: translate('resources.roles.permissionGroups.reservation-policies.label'),
        icon: <Policy />,
        color: '#5C6BC0',
        permissions: [
            { id: 'reservation-policies:read:all', label: translate('resources.roles.permissionGroups.reservation-policies.permissions.reservation-policies:read:all.label'), description: translate('resources.roles.permissionGroups.reservation-policies.permissions.reservation-policies:read:all.description') },
            { id: 'reservation-policies:write:all', label: translate('resources.roles.permissionGroups.reservation-policies.permissions.reservation-policies:write:all.label'), description: translate('resources.roles.permissionGroups.reservation-policies.permissions.reservation-policies:write:all.description') },
        ]
    },
    'user-groups': {
        label: translate('resources.roles.permissionGroups.user-groups.label'),
        icon: <Group />,
        color: '#9C27B0',
        permissions: [
            { id: 'user-groups:read:all', label: translate('resources.roles.permissionGroups.user-groups.permissions.user-groups:read:all.label'), description: translate('resources.roles.permissionGroups.user-groups.permissions.user-groups:read:all.description') },
            { id: 'user-groups:write:all', label: translate('resources.roles.permissionGroups.user-groups.permissions.user-groups:write:all.label'), description: translate('resources.roles.permissionGroups.user-groups.permissions.user-groups:write:all.description') },
        ]
    },
    'app-users': {
        label: translate('resources.roles.permissionGroups.app-users.label'),
        icon: <People />,
        color: '#00BCD4',
        permissions: [
            { id: 'app-users:read:all', label: translate('resources.roles.permissionGroups.app-users.permissions.app-users:read:all.label'), description: translate('resources.roles.permissionGroups.app-users.permissions.app-users:read:all.description') },
            { id: 'app-users:write:all', label: translate('resources.roles.permissionGroups.app-users.permissions.app-users:write:all.label'), description: translate('resources.roles.permissionGroups.app-users.permissions.app-users:write:all.description') },
            { id: 'app-users:admin:all', label: translate('resources.roles.permissionGroups.app-users.permissions.app-users:admin:all.label'), description: translate('resources.roles.permissionGroups.app-users.permissions.app-users:admin:all.description') },
        ]
    },
    'app-account-deletion': {
        label: translate('resources.roles.permissionGroups.app-account-deletion.label') || 'App Account Deletion',
        icon: <PersonRemove />,
        color: '#E53935',
        permissions: [
            { id: 'account_deletion_requests:process:all', label: translate('resources.roles.permissionGroups.app-account-deletion.permissions.account_deletion_requests:process:all.label') || 'Process account deletion requests', description: translate('resources.roles.permissionGroups.app-account-deletion.permissions.account_deletion_requests:process:all.description') || 'View app account deletion requests and process them (includes data anonymization)' },
        ]
    },
    'app-versions': {
        label: translate('resources.roles.permissionGroups.app-versions.label'),
        icon: <SystemUpdate />,
        color: '#1976d2',
        permissions: [
            { id: 'app-versions:read:all', label: translate('resources.roles.permissionGroups.app-versions.permissions.app-versions:read:all.label'), description: translate('resources.roles.permissionGroups.app-versions.permissions.app-versions:read:all.description') },
            { id: 'app-versions:write:all', label: translate('resources.roles.permissionGroups.app-versions.permissions.app-versions:write:all.label'), description: translate('resources.roles.permissionGroups.app-versions.permissions.app-versions:write:all.description') },
        ]
    },
    wallet: {
        label: translate('resources.roles.permissionGroups.wallet.label'),
        icon: <AccountBalanceWallet />,
        color: '#4CAF50',
        permissions: [
            { id: 'wallet:read:all', label: translate('resources.roles.permissionGroups.wallet.permissions.wallet:read:all.label'), description: translate('resources.roles.permissionGroups.wallet.permissions.wallet:read:all.description') },
            { id: 'wallet:write:all', label: translate('resources.roles.permissionGroups.wallet.permissions.wallet:write:all.label'), description: translate('resources.roles.permissionGroups.wallet.permissions.wallet:write:all.description') },
            { id: 'wallet:admin:all', label: translate('resources.roles.permissionGroups.wallet.permissions.wallet:admin:all.label'), description: translate('resources.roles.permissionGroups.wallet.permissions.wallet:admin:all.description') },
        ]
    },
    settings: {
        label: translate('resources.roles.permissionGroups.settings.label'),
        icon: <Settings />,
        color: '#795548',
        permissions: [
            { id: 'settings:read:all', label: translate('resources.roles.permissionGroups.settings.permissions.settings:read:all.label'), description: translate('resources.roles.permissionGroups.settings.permissions.settings:read:all.description') },
            { id: 'settings:write:all', label: translate('resources.roles.permissionGroups.settings.permissions.settings:write:all.label'), description: translate('resources.roles.permissionGroups.settings.permissions.settings:write:all.description') },
            { id: 'settings:admin:all', label: translate('resources.roles.permissionGroups.settings.permissions.settings:admin:all.label'), description: translate('resources.roles.permissionGroups.settings.permissions.settings:admin:all.description') },
        ]
    },
    audit: {
        label: translate('resources.roles.permissionGroups.audit.label'),
        icon: <Description />,
        color: '#607D8B',
        permissions: [
            { id: 'audit:read:all', label: translate('resources.roles.permissionGroups.audit.permissions.audit:read:all.label'), description: translate('resources.roles.permissionGroups.audit.permissions.audit:read:all.description') },
            { id: 'audit:write:all', label: translate('resources.roles.permissionGroups.audit.permissions.audit:write:all.label'), description: translate('resources.roles.permissionGroups.audit.permissions.audit:write:all.description') },
            { id: 'audit:admin:all', label: translate('resources.roles.permissionGroups.audit.permissions.audit:admin:all.label'), description: translate('resources.roles.permissionGroups.audit.permissions.audit:admin:all.description') },
        ]
    },
    reports: {
        label: translate('resources.roles.permissionGroups.reports.label'),
        icon: <Assessment />,
        color: '#E91E63',
        permissions: [
            { id: 'reports:revenue:read', label: translate('resources.roles.permissionGroups.reports.permissions.reports:revenue:read.label') || 'Revenue Analysis', description: translate('resources.roles.permissionGroups.reports.permissions.reports:revenue:read.description') || 'View the revenue analysis report' },
            { id: 'reports:transactions:read', label: translate('resources.roles.permissionGroups.reports.permissions.reports:transactions:read.label') || 'Transaction Volume', description: translate('resources.roles.permissionGroups.reports.permissions.reports:transactions:read.description') || 'View the transaction volume report' },
            { id: 'reports:sessions:read', label: translate('resources.roles.permissionGroups.reports.permissions.reports:sessions:read.label') || 'Session Analytics', description: translate('resources.roles.permissionGroups.reports.permissions.reports:sessions:read.description') || 'View the session analytics report' },
            { id: 'reports:charger-utilization:read', label: translate('resources.roles.permissionGroups.reports.permissions.reports:charger-utilization:read.label') || 'Charger Utilization', description: translate('resources.roles.permissionGroups.reports.permissions.reports:charger-utilization:read.description') || 'View the charger utilization report' },
            { id: 'reports:energy-consumption:read', label: translate('resources.roles.permissionGroups.reports.permissions.reports:energy-consumption:read.label') || 'Energy Consumption', description: translate('resources.roles.permissionGroups.reports.permissions.reports:energy-consumption:read.description') || 'View the energy consumption report' },
            { id: 'reports:peak-usage:read', label: translate('resources.roles.permissionGroups.reports.permissions.reports:peak-usage:read.label') || 'Peak Usage Analysis', description: translate('resources.roles.permissionGroups.reports.permissions.reports:peak-usage:read.description') || 'View the peak usage analysis report' },
            { id: 'reports:user-activity:read', label: translate('resources.roles.permissionGroups.reports.permissions.reports:user-activity:read.label') || 'User Activity', description: translate('resources.roles.permissionGroups.reports.permissions.reports:user-activity:read.description') || 'View the user activity report' },
            { id: 'reports:vendor-performance:read', label: translate('resources.roles.permissionGroups.reports.permissions.reports:vendor-performance:read.label') || 'Vendor Performance', description: translate('resources.roles.permissionGroups.reports.permissions.reports:vendor-performance:read.description') || 'View the vendor performance report (admin only)' },
            { id: 'reports:site-comparison:read', label: translate('resources.roles.permissionGroups.reports.permissions.reports:site-comparison:read.label') || 'Site Comparison', description: translate('resources.roles.permissionGroups.reports.permissions.reports:site-comparison:read.description') || 'View the site comparison report' },
            { id: 'reports:total-sessions:read', label: translate('resources.roles.permissionGroups.reports.permissions.reports:total-sessions:read.label') || 'Total Sessions', description: translate('resources.roles.permissionGroups.reports.permissions.reports:total-sessions:read.description') || 'See total session counts (including zero-energy sessions) in reports and exports' },
            { id: 'reports:kiosk:read', label: translate('resources.roles.permissionGroups.reports.permissions.reports:kiosk:read.label') || 'Kiosk', description: translate('resources.roles.permissionGroups.reports.permissions.reports:kiosk:read.description') || 'View the kiosk report' },
        ]
    },
    notifications: {
        label: translate('resources.roles.permissionGroups.notifications.label'),
        icon: <Notifications />,
        color: '#FF5722',
        permissions: [
            { id: 'notifications:read:all', label: translate('resources.roles.permissionGroups.notifications.permissions.notifications:read:all.label'), description: translate('resources.roles.permissionGroups.notifications.permissions.notifications:read:all.description') },
            { id: 'notifications:write:all', label: translate('resources.roles.permissionGroups.notifications.permissions.notifications:write:all.label'), description: translate('resources.roles.permissionGroups.notifications.permissions.notifications:write:all.description') },
            { id: 'notifications:manage:all', label: translate('resources.roles.permissionGroups.notifications.permissions.notifications:manage:all.label'), description: translate('resources.roles.permissionGroups.notifications.permissions.notifications:manage:all.description') },
        ]
    },
    'email-routing': {
        label: translate('resources.roles.permissionGroups.email-routing.label') || 'Email Provider Routing',
        icon: <AlternateEmail />,
        color: '#009A75',
        permissions: [
            { id: 'email_routing:read:all', label: translate('resources.roles.permissionGroups.email-routing.permissions.email_routing:read:all.label') || 'View email routing', description: translate('resources.roles.permissionGroups.email-routing.permissions.email_routing:read:all.description') || 'Can view per-template email provider routing and the default provider' },
            { id: 'email_routing:write:all', label: translate('resources.roles.permissionGroups.email-routing.permissions.email_routing:write:all.label') || 'Manage email routing', description: translate('resources.roles.permissionGroups.email-routing.permissions.email_routing:write:all.description') || 'Can change per-template email provider, default provider, and enable/disable templates' },
        ]
    },
    messages: {
        label: translate('resources.roles.permissionGroups.messages.label') || 'Messages',
        icon: <Notifications />,
        color: '#673AB7',
        permissions: [
            { id: 'messages:read:all', label: translate('resources.roles.permissionGroups.messages.permissions.messages:read:all.label') || 'Read Messages', description: translate('resources.roles.permissionGroups.messages.permissions.messages:read:all.description') || 'View and list all messages' },
            { id: 'messages:write:all', label: translate('resources.roles.permissionGroups.messages.permissions.messages:write:all.label') || 'Write Messages', description: translate('resources.roles.permissionGroups.messages.permissions.messages:write:all.description') || 'Create and edit messages' },
            { id: 'messages:send:all', label: translate('resources.roles.permissionGroups.messages.permissions.messages:send:all.label') || 'Send Messages', description: translate('resources.roles.permissionGroups.messages.permissions.messages:send:all.description') || 'Send messages and push notifications' },
            { id: 'messages:admin:all', label: translate('resources.roles.permissionGroups.messages.permissions.messages:admin:all.label') || 'Admin Messages', description: translate('resources.roles.permissionGroups.messages.permissions.messages:admin:all.description') || 'Full control over messages including deletion' },
        ]
    },
    banners: {
        label: translate('resources.roles.permissionGroups.banners.label') || 'Promotion Banners',
        icon: <Campaign />,
        color: '#FF6B35',
        permissions: [
            { id: 'banners:read:all', label: translate('resources.roles.permissionGroups.banners.permissions.banners:read:all.label') || 'Read Banners', description: translate('resources.roles.permissionGroups.banners.permissions.banners:read:all.description') || 'View and list all promotion banners' },
            { id: 'banners:write:all', label: translate('resources.roles.permissionGroups.banners.permissions.banners:write:all.label') || 'Write Banners', description: translate('resources.roles.permissionGroups.banners.permissions.banners:write:all.description') || 'Create and edit promotion banners' },
            { id: 'banners:analytics:all', label: translate('resources.roles.permissionGroups.banners.permissions.banners:analytics:all.label') || 'Banner Analytics', description: translate('resources.roles.permissionGroups.banners.permissions.banners:analytics:all.description') || 'View banner performance analytics and metrics' },
            { id: 'banners:admin:all', label: translate('resources.roles.permissionGroups.banners.permissions.banners:admin:all.label') || 'Admin Banners', description: translate('resources.roles.permissionGroups.banners.permissions.banners:admin:all.description') || 'Full control over banners including deletion and targeting' },
        ]
    },
    promotions: {
        label: translate('resources.roles.permissionGroups.promotions.label') || 'Promotions & Coupons',
        icon: <LocalOffer />,
        color: '#FF4081',
        permissions: [
            { id: 'promotions:read:all', label: translate('resources.roles.permissionGroups.promotions.permissions.promotions:read:all.label') || 'Read Promotions', description: translate('resources.roles.permissionGroups.promotions.permissions.promotions:read:all.description') || 'View and list all promotions, coupons, and redemptions' },
            { id: 'promotions:write:all', label: translate('resources.roles.permissionGroups.promotions.permissions.promotions:write:all.label') || 'Write Promotions', description: translate('resources.roles.permissionGroups.promotions.permissions.promotions:write:all.description') || 'Create, edit, and manage promotions and coupon codes' },
            { id: 'promotions:export:all', label: translate('resources.roles.permissionGroups.promotions.permissions.promotions:export:all.label') || 'Export Data', description: translate('resources.roles.permissionGroups.promotions.permissions.promotions:export:all.description') || 'Export promotion codes and redemption reports' },
            { id: 'promotions:admin:all', label: translate('resources.roles.permissionGroups.promotions.permissions.promotions:admin:all.label') || 'Admin Promotions', description: translate('resources.roles.permissionGroups.promotions.permissions.promotions:admin:all.description') || 'Full control including deletion and advanced analytics' },
        ]
    },
    'ocpp-servers': {
        label: translate('resources.roles.permissionGroups.ocpp-servers.label'),
        icon: <Storage />,
        color: '#2196F3',
        permissions: [
            { id: 'ocpp-servers:read:all', label: translate('resources.roles.permissionGroups.ocpp-servers.permissions.ocpp-servers:read:all.label'), description: translate('resources.roles.permissionGroups.ocpp-servers.permissions.ocpp-servers:read:all.description') },
            { id: 'ocpp-servers:write:all', label: translate('resources.roles.permissionGroups.ocpp-servers.permissions.ocpp-servers:write:all.label'), description: translate('resources.roles.permissionGroups.ocpp-servers.permissions.ocpp-servers:write:all.description') },
            { id: 'ocpp-servers:admin:all', label: translate('resources.roles.permissionGroups.ocpp-servers.permissions.ocpp-servers:admin:all.label'), description: translate('resources.roles.permissionGroups.ocpp-servers.permissions.ocpp-servers:admin:all.description') },
        ]
    },
    'ocpp-templates': {
        label: translate('resources.roles.permissionGroups.ocpp-templates.label') || 'OCPP Templates',
        icon: <Settings />,
        color: '#0D47A1',
        permissions: [
            { id: 'ocpp-templates:read:all', label: translate('resources.roles.permissionGroups.ocpp-templates.permissions.ocpp-templates:read:all.label') || 'Read OCPP Templates', description: translate('resources.roles.permissionGroups.ocpp-templates.permissions.ocpp-templates:read:all.description') || 'View all OCPP configuration templates' },
            { id: 'ocpp-templates:write:all', label: translate('resources.roles.permissionGroups.ocpp-templates.permissions.ocpp-templates:write:all.label') || 'Write OCPP Templates', description: translate('resources.roles.permissionGroups.ocpp-templates.permissions.ocpp-templates:write:all.description') || 'Create and edit OCPP configuration templates' },
            { id: 'ocpp-templates:admin:all', label: translate('resources.roles.permissionGroups.ocpp-templates.permissions.ocpp-templates:admin:all.label') || 'Admin OCPP Templates', description: translate('resources.roles.permissionGroups.ocpp-templates.permissions.ocpp-templates:admin:all.description') || 'Full control over OCPP templates including deletion and setting defaults' },
        ]
    },
    'supabase-connections': {
        label: translate('resources.roles.permissionGroups.supabase-connections.label'),
        icon: <CloudQueue />,
        color: '#00BCD4',
        permissions: [
            { id: 'supabase-connections:read:all', label: translate('resources.roles.permissionGroups.supabase-connections.permissions.supabase-connections:read:all.label'), description: translate('resources.roles.permissionGroups.supabase-connections.permissions.supabase-connections:read:all.description') },
            { id: 'supabase-connections:write:all', label: translate('resources.roles.permissionGroups.supabase-connections.permissions.supabase-connections:write:all.label'), description: translate('resources.roles.permissionGroups.supabase-connections.permissions.supabase-connections:write:all.description') },
            { id: 'supabase-connections:admin:all', label: translate('resources.roles.permissionGroups.supabase-connections.permissions.supabase-connections:admin:all.label'), description: translate('resources.roles.permissionGroups.supabase-connections.permissions.supabase-connections:admin:all.description') },
        ]
    },
    'lms-logs': {
        label: translate('resources.roles.permissionGroups.lms-logs.label'),
        icon: <Timeline />,
        color: '#673AB7',
        permissions: [
            { id: 'lms-logs:read:all', label: translate('resources.roles.permissionGroups.lms-logs.permissions.lms-logs:read:all.label'), description: translate('resources.roles.permissionGroups.lms-logs.permissions.lms-logs:read:all.description') },
            { id: 'lms-logs:write:all', label: translate('resources.roles.permissionGroups.lms-logs.permissions.lms-logs:write:all.label'), description: translate('resources.roles.permissionGroups.lms-logs.permissions.lms-logs:write:all.description') },
            { id: 'lms-logs:admin:all', label: translate('resources.roles.permissionGroups.lms-logs.permissions.lms-logs:admin:all.label'), description: translate('resources.roles.permissionGroups.lms-logs.permissions.lms-logs:admin:all.description') },
        ]
    },
    'ocpp-logs': {
        label: translate('resources.roles.permissionGroups.ocpp-logs.label') || 'OCPP Logs',
        icon: <Timeline />,
        color: '#3F51B5',
        permissions: [
            { id: 'ocpp-logs:read:all', label: translate('resources.roles.permissionGroups.ocpp-logs.permissions.ocpp-logs:read:all.label') || 'View OCPP Logs', description: translate('resources.roles.permissionGroups.ocpp-logs.permissions.ocpp-logs:read:all.description') || 'Query and inspect raw OCPP protocol messages by charger and time range' },
        ]
    },
    'lms-templates': {
        label: translate('resources.roles.permissionGroups.lms-templates.label') || 'LMS Templates',
        icon: <Settings />,
        color: '#00897B',
        permissions: [
            { id: 'lms-templates:read:all', label: translate('resources.roles.permissionGroups.lms-templates.permissions.lms-templates:read:all.label') || 'Read LMS Templates', description: translate('resources.roles.permissionGroups.lms-templates.permissions.lms-templates:read:all.description') || 'View and list all load management system templates' },
            { id: 'lms-templates:write:all', label: translate('resources.roles.permissionGroups.lms-templates.permissions.lms-templates:write:all.label') || 'Write LMS Templates', description: translate('resources.roles.permissionGroups.lms-templates.permissions.lms-templates:write:all.description') || 'Create and edit load management system templates' },
            { id: 'lms-templates:admin:all', label: translate('resources.roles.permissionGroups.lms-templates.permissions.lms-templates:admin:all.label') || 'Admin LMS Templates', description: translate('resources.roles.permissionGroups.lms-templates.permissions.lms-templates:admin:all.description') || 'Full control over LMS templates including deletion' },
        ]
    },
    vendors: {
        label: translate('resources.roles.permissionGroups.vendors.label') || 'Vendors',
        icon: <People />,
        color: '#9C27B0',
        permissions: [
            { id: 'vendors:read:all', label: translate('resources.roles.permissionGroups.vendors.permissions.vendors:read:all.label') || 'Read Vendors', description: translate('resources.roles.permissionGroups.vendors.permissions.vendors:read:all.description') || 'View and list all vendors' },
            { id: 'vendors:write:all', label: translate('resources.roles.permissionGroups.vendors.permissions.vendors:write:all.label') || 'Write Vendors', description: translate('resources.roles.permissionGroups.vendors.permissions.vendors:write:all.description') || 'Create and edit vendors' },
            { id: 'vendors:admin:all', label: translate('resources.roles.permissionGroups.vendors.permissions.vendors:admin:all.label') || 'Admin Vendors', description: translate('resources.roles.permissionGroups.vendors.permissions.vendors:admin:all.description') || 'Full control over vendors including deletion' },
        ]
    },
    'kiosk-transactions': {
        label: translate('resources.roles.permissionGroups.kiosk-transactions.label') || 'Kiosk Transactions',
        icon: <Storefront />,
        color: '#00796B',
        permissions: [
            { id: 'kiosk-transactions:read:all', label: translate('resources.roles.permissionGroups.kiosk-transactions.permissions.kiosk-transactions:read:all.label') || 'Read Kiosk Transactions', description: translate('resources.roles.permissionGroups.kiosk-transactions.permissions.kiosk-transactions:read:all.description') || 'View and export kiosk payments and their refunds, and search by Octopus number' },
            { id: 'kiosk-transactions:write:all', label: translate('resources.roles.permissionGroups.kiosk-transactions.permissions.kiosk-transactions:write:all.label') || 'Write Kiosk Transactions', description: translate('resources.roles.permissionGroups.kiosk-transactions.permissions.kiosk-transactions:write:all.description') || 'Mark an expired Octopus refund refunded by hand' },
        ]
    },
    'kiosk-settlements': {
        label: translate('resources.roles.permissionGroups.kiosk-settlements.label') || 'Octopus settlements',
        icon: <Storefront />,
        color: '#00796B',
        permissions: [
            { id: 'kiosk-settlements:read:all', label: translate('resources.roles.permissionGroups.kiosk-settlements.permissions.kiosk-settlements:read:all.label') || 'Read Octopus settlements', description: translate('resources.roles.permissionGroups.kiosk-settlements.permissions.kiosk-settlements:read:all.description') || 'View and export each Octopus reader settlement beside our record, and see overdue settlements' },
        ]
    },
    'kiosk-releases': {
        label: translate('resources.roles.permissionGroups.kiosk-releases.label') || 'Kiosk releases',
        icon: <SystemUpdate />,
        color: '#00695C',
        permissions: [
            { id: 'kiosk-releases:read:all', label: translate('resources.roles.permissionGroups.kiosk-releases.permissions.kiosk-releases:read:all.label') || 'Read kiosk releases', description: translate('resources.roles.permissionGroups.kiosk-releases.permissions.kiosk-releases:read:all.description') || 'View kiosk software releases, rollouts and install progress' },
            { id: 'kiosk-releases:write:all', label: translate('resources.roles.permissionGroups.kiosk-releases.permissions.kiosk-releases:write:all.label') || 'Write kiosk releases', description: translate('resources.roles.permissionGroups.kiosk-releases.permissions.kiosk-releases:write:all.description') || 'Upload, roll out, pause and revoke kiosk software releases' },
        ]
    },
    'kiosk-release-keys': {
        label: translate('resources.roles.permissionGroups.kiosk-release-keys.label') || 'Kiosk release keys',
        icon: <VpnKey />,
        color: '#00695C',
        permissions: [
            { id: 'kiosk-release-keys:write:all', label: translate('resources.roles.permissionGroups.kiosk-release-keys.permissions.kiosk-release-keys:write:all.label') || 'Write kiosk release keys', description: translate('resources.roles.permissionGroups.kiosk-release-keys.permissions.kiosk-release-keys:write:all.description') || 'Create, rotate and replace the kiosk release key' },
        ]
    },
    'kiosk-devices': {
        label: translate('resources.roles.permissionGroups.kiosk-devices.label') || 'Kiosks',
        icon: <Storefront />,
        color: '#00796B',
        permissions: [
            { id: 'kiosk-devices:read:all', label: translate('resources.roles.permissionGroups.kiosk-devices.permissions.kiosk-devices:read:all.label') || 'Read Kiosks', description: translate('resources.roles.permissionGroups.kiosk-devices.permissions.kiosk-devices:read:all.description') || 'View kiosks, their health and settings' },
            { id: 'kiosk-devices:write:all', label: translate('resources.roles.permissionGroups.kiosk-devices.permissions.kiosk-devices:write:all.label') || 'Write Kiosks', description: translate('resources.roles.permissionGroups.kiosk-devices.permissions.kiosk-devices:write:all.description') || 'Register, enrol, configure and disable kiosks' },
        ]
    },
    'kiosk-alerts': {
        label: translate('resources.roles.permissionGroups.kiosk-alerts.label') || 'Kiosk alerts',
        icon: <Storefront />,
        color: '#00796B',
        permissions: [
            { id: 'kiosk-alerts:read:all', label: translate('resources.roles.permissionGroups.kiosk-alerts.permissions.kiosk-alerts:read:all.label') || 'Read kiosk alerts', description: translate('resources.roles.permissionGroups.kiosk-alerts.permissions.kiosk-alerts:read:all.description') || 'See kiosk alerts and their counts. Site owners see only the day-to-day alerts of their own sites' },
            { id: 'kiosk-alerts:write:all', label: translate('resources.roles.permissionGroups.kiosk-alerts.permissions.kiosk-alerts:write:all.label') || 'Write kiosk alerts', description: translate('resources.roles.permissionGroups.kiosk-alerts.permissions.kiosk-alerts:write:all.description') || 'Mark a kiosk alert handled with a note' },
        ]
    },
    'rfid-users': {
        label: translate('resources.roles.permissionGroups.rfid-users.label') || 'RFID Users',
        icon: <People />,
        color: '#3F51B5',
        permissions: [
            { id: 'rfid-users:read:all', label: translate('resources.roles.permissionGroups.rfid-users.permissions.rfid-users:read:all.label') || 'Read RFID Users', description: translate('resources.roles.permissionGroups.rfid-users.permissions.rfid-users:read:all.description') || 'View and list all RFID users' },
            { id: 'rfid-users:write:all', label: translate('resources.roles.permissionGroups.rfid-users.permissions.rfid-users:write:all.label') || 'Write RFID Users', description: translate('resources.roles.permissionGroups.rfid-users.permissions.rfid-users:write:all.description') || 'Create and edit RFID users' },
            { id: 'rfid-users:admin:all', label: translate('resources.roles.permissionGroups.rfid-users.permissions.rfid-users:admin:all.label') || 'Admin RFID Users', description: translate('resources.roles.permissionGroups.rfid-users.permissions.rfid-users:admin:all.description') || 'Full control over RFID users including deletion' },
        ]
    },
    'rfid-cards': {
        label: translate('resources.roles.permissionGroups.rfid-cards.label') || 'RFID Cards',
        icon: <Security />,
        color: '#E91E63',
        permissions: [
            { id: 'rfid-cards:read:all', label: translate('resources.roles.permissionGroups.rfid-cards.permissions.rfid-cards:read:all.label') || 'Read RFID Cards', description: translate('resources.roles.permissionGroups.rfid-cards.permissions.rfid-cards:read:all.description') || 'View and list all RFID cards' },
            { id: 'rfid-cards:write:all', label: translate('resources.roles.permissionGroups.rfid-cards.permissions.rfid-cards:write:all.label') || 'Write RFID Cards', description: translate('resources.roles.permissionGroups.rfid-cards.permissions.rfid-cards:write:all.description') || 'Create and edit RFID cards' },
            { id: 'rfid-cards:admin:all', label: translate('resources.roles.permissionGroups.rfid-cards.permissions.rfid-cards:admin:all.label') || 'Admin RFID Cards', description: translate('resources.roles.permissionGroups.rfid-cards.permissions.rfid-cards:admin:all.description') || 'Full control over RFID cards including bulk operations' },
        ]
    },
    'app-transactions': {
        label: translate('resources.roles.permissionGroups.app-transactions.label') || 'App Transactions',
        icon: <Receipt />,
        color: '#4CAF50',
        permissions: [
            { id: 'app-transactions:read:all', label: translate('resources.roles.permissionGroups.app-transactions.permissions.app-transactions:read:all.label') || 'Read App Transactions', description: translate('resources.roles.permissionGroups.app-transactions.permissions.app-transactions:read:all.description') || 'View and list all app payment transactions' },
            { id: 'app-transactions:write:all', label: translate('resources.roles.permissionGroups.app-transactions.permissions.app-transactions:write:all.label') || 'Write App Transactions', description: translate('resources.roles.permissionGroups.app-transactions.permissions.app-transactions:write:all.description') || 'Create and edit app payment transactions' },
            { id: 'app-transactions:export:all', label: translate('resources.roles.permissionGroups.app-transactions.permissions.app-transactions:export:all.label') || 'Export App Transactions', description: translate('resources.roles.permissionGroups.app-transactions.permissions.app-transactions:export:all.description') || 'Export app transaction data and reports' },
            { id: 'app-transactions:admin:all', label: translate('resources.roles.permissionGroups.app-transactions.permissions.app-transactions:admin:all.label') || 'Admin App Transactions', description: translate('resources.roles.permissionGroups.app-transactions.permissions.app-transactions:admin:all.description') || 'Full control over app transactions including refunds and adjustments' },
        ]
    },
    'app-topups': {
        label: translate('resources.roles.permissionGroups.app-topups.label') || 'App Top-Ups',
        icon: <AccountBalanceWallet />,
        color: '#00BCD4',
        permissions: [
            { id: 'app-topups:read:all', label: translate('resources.roles.permissionGroups.app-topups.permissions.app-topups:read:all.label') || 'Read App Top-Ups', description: translate('resources.roles.permissionGroups.app-topups.permissions.app-topups:read:all.description') || 'View and list wallet top-up transactions and user wallet balances' },
            { id: 'app-topups:export:all', label: translate('resources.roles.permissionGroups.app-topups.permissions.app-topups:export:all.label') || 'Export App Top-Ups', description: translate('resources.roles.permissionGroups.app-topups.permissions.app-topups:export:all.description') || 'Export top-up data to CSV' },
            { id: 'app-topups:admin:all', label: translate('resources.roles.permissionGroups.app-topups.permissions.app-topups:admin:all.label') || 'Admin App Top-Ups', description: translate('resources.roles.permissionGroups.app-topups.permissions.app-topups:admin:all.description') || 'Full control over top-ups including refunds' },
        ]
    },
    'rfid-transactions': {
        label: translate('resources.roles.permissionGroups.rfid-transactions.label') || 'RFID Transactions',
        icon: <AccountBalanceWallet />,
        color: '#FF9800',
        permissions: [
            { id: 'rfid-transactions:read:all', label: translate('resources.roles.permissionGroups.rfid-transactions.permissions.rfid-transactions:read:all.label') || 'Read RFID Transactions', description: translate('resources.roles.permissionGroups.rfid-transactions.permissions.rfid-transactions:read:all.description') || 'View and list all RFID payment transactions' },
            { id: 'rfid-transactions:write:all', label: translate('resources.roles.permissionGroups.rfid-transactions.permissions.rfid-transactions:write:all.label') || 'Write RFID Transactions', description: translate('resources.roles.permissionGroups.rfid-transactions.permissions.rfid-transactions:write:all.description') || 'Create and edit RFID payment transactions' },
            { id: 'rfid-transactions:export:all', label: translate('resources.roles.permissionGroups.rfid-transactions.permissions.rfid-transactions:export:all.label') || 'Export RFID Transactions', description: translate('resources.roles.permissionGroups.rfid-transactions.permissions.rfid-transactions:export:all.description') || 'Export RFID transaction data and reports' },
            { id: 'rfid-transactions:admin:all', label: translate('resources.roles.permissionGroups.rfid-transactions.permissions.rfid-transactions:admin:all.label') || 'Admin RFID Transactions', description: translate('resources.roles.permissionGroups.rfid-transactions.permissions.rfid-transactions:admin:all.description') || 'Full control over RFID transactions including refunds and adjustments' },
        ]
    },
    'active-sessions': {
        label: translate('resources.roles.permissionGroups.active-sessions.label') || 'Active Sessions',
        icon: <Bolt />,
        color: '#2E7D32',
        permissions: [
            { id: 'active-sessions:read:all', label: translate('resources.roles.permissionGroups.active-sessions.permissions.active-sessions:read:all.label') || 'Read Active Sessions', description: translate('resources.roles.permissionGroups.active-sessions.permissions.active-sessions:read:all.description') || 'View and monitor all active charging sessions' },
            { id: 'charging_sessions:manual_start', label: translate('resources.roles.permissionGroups.active-sessions.permissions.charging_sessions:manual_start.label') || 'Manual Start Sessions', description: translate('resources.roles.permissionGroups.active-sessions.permissions.charging_sessions:manual_start.description') || 'Manually start charging sessions on behalf of users or standalone for testing/maintenance' },
            { id: 'charging_sessions:manual_stop', label: translate('resources.roles.permissionGroups.active-sessions.permissions.charging_sessions:manual_stop.label') || 'Manual Stop Sessions', description: translate('resources.roles.permissionGroups.active-sessions.permissions.charging_sessions:manual_stop.description') || 'Manually stop any active charging session with reason documentation' },
            { id: 'charging_sessions:force_complete', label: translate('resources.roles.permissionGroups.active-sessions.permissions.charging_sessions:force_complete.label') || 'Force Complete Sessions', description: translate('resources.roles.permissionGroups.active-sessions.permissions.charging_sessions:force_complete.description') || 'Manually force-complete stuck sessions when charger is offline or unreachable (network outage, hardware failure)' },
        ]
    },
    'charging-sessions': {
        label: translate('resources.roles.permissionGroups.charging-sessions.label') || 'Charging History',
        icon: <Bolt />,
        color: '#1565C0',
        permissions: [
            { id: 'charging-sessions:read:all', label: translate('resources.roles.permissionGroups.charging-sessions.permissions.charging-sessions:read:all.label') || 'View Charging History', description: translate('resources.roles.permissionGroups.charging-sessions.permissions.charging-sessions:read:all.description') || 'Can view all archived charging sessions and their details' },
            { id: 'charging-sessions:export:all', label: translate('resources.roles.permissionGroups.charging-sessions.permissions.charging-sessions:export:all.label') || 'Export Charging History', description: translate('resources.roles.permissionGroups.charging-sessions.permissions.charging-sessions:export:all.description') || 'Can export charging history (per-transaction usage report) to Excel' },
            { id: 'charging-sessions:view-analytics:all', label: translate('resources.roles.permissionGroups.charging-sessions.permissions.charging-sessions:view-analytics:all.label') || 'View Analytics Tab', description: translate('resources.roles.permissionGroups.charging-sessions.permissions.charging-sessions:view-analytics:all.description') || 'Can view the Analytics tab (meter-value charts) on a charging-session detail page' },
            { id: 'charging-sessions:view-payment:all', label: translate('resources.roles.permissionGroups.charging-sessions.permissions.charging-sessions:view-payment:all.label') || 'View Payment Tab', description: translate('resources.roles.permissionGroups.charging-sessions.permissions.charging-sessions:view-payment:all.description') || 'Can view the Payment tab (cost breakdown + payment details) on a charging-session detail page' },
            { id: 'charging-sessions:view-ocpp:all', label: translate('resources.roles.permissionGroups.charging-sessions.permissions.charging-sessions:view-ocpp:all.label') || 'View OCPP Logs', description: translate('resources.roles.permissionGroups.charging-sessions.permissions.charging-sessions:view-ocpp:all.description') || 'Can view the OCPP Logs tab and the "View OCPP Logs" button. Required to call the per-session OCPP logs API.' },
            { id: 'charging-sessions:view-technical:all', label: translate('resources.roles.permissionGroups.charging-sessions.permissions.charging-sessions:view-technical:all.label') || 'View Technical Tab', description: translate('resources.roles.permissionGroups.charging-sessions.permissions.charging-sessions:view-technical:all.description') || 'Can view the Technical tab (temperature / error counts / archive metadata) on a charging-session detail page' },
        ]
    },
    'charger-firmware': {
        label: translate('resources.roles.permissionGroups.charger-firmware.label') || 'Charger Firmware',
        icon: <CloudDownload />,
        color: '#6A1B9A',
        permissions: [
            { id: 'charger-firmware:read:all', label: translate('resources.roles.permissionGroups.charger-firmware.permissions.charger-firmware:read:all.label') || 'View Firmware Files', description: translate('resources.roles.permissionGroups.charger-firmware.permissions.charger-firmware:read:all.description') || 'Can view all firmware files and their metadata' },
            { id: 'charger-firmware:write:all', label: translate('resources.roles.permissionGroups.charger-firmware.permissions.charger-firmware:write:all.label') || 'Upload Firmware Files', description: translate('resources.roles.permissionGroups.charger-firmware.permissions.charger-firmware:write:all.description') || 'Can upload new firmware files and edit firmware metadata' },
            { id: 'charger-firmware:admin:all', label: translate('resources.roles.permissionGroups.charger-firmware.permissions.charger-firmware:admin:all.label') || 'Delete Firmware Files', description: translate('resources.roles.permissionGroups.charger-firmware.permissions.charger-firmware:admin:all.description') || 'Can delete firmware files from the system' },
        ]
    },
    'firmware-updates': {
        label: translate('resources.roles.permissionGroups.firmware-updates.label') || 'Firmware Updates',
        icon: <SystemUpdate />,
        color: '#D32F2F',
        permissions: [
            { id: 'firmware-updates:read:all', label: translate('resources.roles.permissionGroups.firmware-updates.permissions.firmware-updates:read:all.label') || 'View Update Jobs', description: translate('resources.roles.permissionGroups.firmware-updates.permissions.firmware-updates:read:all.description') || 'Can view all firmware update jobs and their status' },
            { id: 'firmware-updates:write:all', label: translate('resources.roles.permissionGroups.firmware-updates.permissions.firmware-updates:write:all.label') || 'Create Update Jobs', description: translate('resources.roles.permissionGroups.firmware-updates.permissions.firmware-updates:write:all.description') || 'Can create firmware update jobs and schedule charger updates' },
            { id: 'firmware-updates:admin:all', label: translate('resources.roles.permissionGroups.firmware-updates.permissions.firmware-updates:admin:all.label') || 'Manage Update Jobs', description: translate('resources.roles.permissionGroups.firmware-updates.permissions.firmware-updates:admin:all.description') || 'Can cancel firmware update jobs and query status' },
        ]
    },
    'fee-templates': {
        label: translate('resources.roles.permissionGroups.fee-templates.label') || 'Fee Templates',
        icon: <AttachMoney />,
        color: '#FF9800',
        permissions: [
            { id: 'fee-templates:read:all', label: translate('resources.roles.permissionGroups.fee-templates.permissions.fee-templates:read:all.label') || 'Read Fee Templates', description: translate('resources.roles.permissionGroups.fee-templates.permissions.fee-templates:read:all.description') || 'View and list all fee templates' },
            { id: 'fee-templates:write:all', label: translate('resources.roles.permissionGroups.fee-templates.permissions.fee-templates:write:all.label') || 'Write Fee Templates', description: translate('resources.roles.permissionGroups.fee-templates.permissions.fee-templates:write:all.description') || 'Create and edit fee templates' },
            { id: 'fee-templates:admin:all', label: translate('resources.roles.permissionGroups.fee-templates.permissions.fee-templates:admin:all.label') || 'Admin Fee Templates', description: translate('resources.roles.permissionGroups.fee-templates.permissions.fee-templates:admin:all.description') || 'Full control over fee templates including deletion' },
        ]
    },
    'fee-schedules': {
        label: translate('resources.roles.permissionGroups.fee-schedules.label') || 'Fee Schedules',
        icon: <AttachMoney />,
        color: '#F57C00',
        permissions: [
            { id: 'fee-schedules:read:all', label: translate('resources.roles.permissionGroups.fee-schedules.permissions.fee-schedules:read:all.label') || 'Read Fee Schedules', description: translate('resources.roles.permissionGroups.fee-schedules.permissions.fee-schedules:read:all.description') || 'View and list all fee schedules' },
            { id: 'fee-schedules:write:all', label: translate('resources.roles.permissionGroups.fee-schedules.permissions.fee-schedules:write:all.label') || 'Write Fee Schedules', description: translate('resources.roles.permissionGroups.fee-schedules.permissions.fee-schedules:write:all.description') || 'Create and edit fee schedules' },
            { id: 'fee-schedules:admin:all', label: translate('resources.roles.permissionGroups.fee-schedules.permissions.fee-schedules:admin:all.label') || 'Admin Fee Schedules', description: translate('resources.roles.permissionGroups.fee-schedules.permissions.fee-schedules:admin:all.description') || 'Full control over fee schedules including deletion' },
        ]
    },
    'payment-settings': {
        label: translate('resources.roles.permissionGroups.payment-settings.label') || 'Payment Settings',
        icon: <AccountBalanceWallet />,
        color: '#00695C',
        permissions: [
            { id: 'payment-settings:read:all', label: translate('resources.roles.permissionGroups.payment-settings.permissions.payment-settings:read:all.label') || 'Read Payment Settings', description: translate('resources.roles.permissionGroups.payment-settings.permissions.payment-settings:read:all.description') || 'View payment gateways and payment methods configuration' },
            { id: 'payment-settings:write:all', label: translate('resources.roles.permissionGroups.payment-settings.permissions.payment-settings:write:all.label') || 'Write Payment Settings', description: translate('resources.roles.permissionGroups.payment-settings.permissions.payment-settings:write:all.description') || 'Edit payment gateway settings, enable/disable payment methods, and reorder payment methods' },
        ]
    },
    'vehicle-catalog': {
        label: translate('resources.roles.permissionGroups.vehicle-catalog.label') || 'Vehicle Catalog',
        icon: <DirectionsCar />,
        color: '#00ACC1',
        permissions: [
            { id: 'vehicle-catalog:read:all', label: translate('resources.roles.permissionGroups.vehicle-catalog.permissions.vehicle-catalog:read:all.label') || 'Read Vehicle Catalog', description: translate('resources.roles.permissionGroups.vehicle-catalog.permissions.vehicle-catalog:read:all.description') || 'View vehicle brands and models' },
            { id: 'vehicle-catalog:write:all', label: translate('resources.roles.permissionGroups.vehicle-catalog.permissions.vehicle-catalog:write:all.label') || 'Write Vehicle Catalog', description: translate('resources.roles.permissionGroups.vehicle-catalog.permissions.vehicle-catalog:write:all.description') || 'Create, edit, and delete vehicle brands and models' },
            { id: 'vehicle-catalog:import:all', label: translate('resources.roles.permissionGroups.vehicle-catalog.permissions.vehicle-catalog:import:all.label') || 'Import Vehicle Catalog', description: translate('resources.roles.permissionGroups.vehicle-catalog.permissions.vehicle-catalog:import:all.description') || 'Bulk import vehicle models from PDF' },
        ]
    },
    'ai-troubleshooting': {
        label: translate('resources.roles.permissionGroups.ai-troubleshooting.label'),
        icon: <Psychology />,
        color: '#6A1B9A',
        permissions: [
            { id: 'ai_troubleshooting:use:all', label: translate('resources.roles.permissionGroups.ai-troubleshooting.permissions.ai_troubleshooting:use:all.label'), description: translate('resources.roles.permissionGroups.ai-troubleshooting.permissions.ai_troubleshooting:use:all.description') },
            { id: 'ai_troubleshooting:manage:all', label: translate('resources.roles.permissionGroups.ai-troubleshooting.permissions.ai_troubleshooting:manage:all.label'), description: translate('resources.roles.permissionGroups.ai-troubleshooting.permissions.ai_troubleshooting:manage:all.description') },
        ]
    },
});

// System roles that cannot be deleted
const SYSTEM_ROLES = ['Super Admin', 'Admin'];

const textWrapSx = {
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
};

const wrappedChipSx = {
    maxWidth: '100%',
    height: 'auto',
    '& .MuiChip-label': {
        whiteSpace: 'normal',
        ...textWrapSx,
        lineHeight: 1.2,
        py: 0.5,
    },
};

const EditToolbar = () => {
    const translate = useTranslate();
    const record = useRecordContext();

    const isSystemRole = SYSTEM_ROLES.includes(record?.name);

    return (
        <>
            <Toolbar
                sx={{
                    backgroundColor: '#FAFAFA',
                    borderTop: '1px solid #E0E0E0',
                    px: { xs: 2, sm: 3 },
                    py: 2,
                    mt: { xs: 2, sm: 4 },
                    flexDirection: { xs: 'column-reverse', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: { xs: 1.5, sm: 0 },
                    justifyContent: 'space-between',
                    '& .MuiButton-root': {
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        width: { xs: '100%', sm: 'auto' },
                        px: { xs: 2, sm: 3 },
                        py: 1.5,
                    },
                }}
            >
                <SaveButton
                    variant="contained"
                    sx={{
                        backgroundColor: '#00C853',
                        '&:hover': {
                            backgroundColor: '#00A843',
                            boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                        },
                    }}
                />
                {!isSystemRole && (
                    <DeleteButton
                        confirmTitle={translate('resources.roles.delete.title')}
                        confirmContent={translate('resources.roles.delete.content')}
                        sx={{
                            color: '#F44336',
                            '&:hover': {
                                backgroundColor: 'rgba(244, 67, 54, 0.08)',
                            },
                        }}
                    />
                )}
            </Toolbar>
        </>
    );
};

const RoleInfo = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const dataProvider = useDataProvider();
    const [userCount, setUserCount] = useState(0);
    
    useEffect(() => {
        if (record?.id) {
            // Get users with this role
            dataProvider.getList('backend-users', {
                filter: { roleId: record.id },
                pagination: { page: 1, perPage: 1 },
                sort: { field: 'id', order: 'ASC' }
            }).then(result => {
                setUserCount(result.total || 0);
            }).catch(() => {
                // If error, just show 0
                setUserCount(0);
            });
        }
    }, [record?.id, dataProvider]);
    
    if (!record) return null;
    
    const isSystemRole = SYSTEM_ROLES.includes(record.name);
    
    return (
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
            {isSystemRole && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={textWrapSx}>
                        {translate('resources.roles.system_role_info')}
                    </Typography>
                </Alert>
            )}

            <Box sx={{ display: 'flex', gap: { xs: 2, sm: 3 }, flexWrap: 'wrap', alignItems: 'stretch' }}>
                <Box sx={{ minWidth: 0, flex: { xs: '1 1 100%', sm: '1 1 180px' } }}>
                    <Typography variant="body2" color="text.secondary" sx={textWrapSx}>
                        {translate('resources.roles.fields.createdAt')}
                    </Typography>
                    <DateField 
                        source="createdAt" 
                        locales="en-US"
                        options={{ 
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        }}
                    />
                </Box>
                <Box sx={{ minWidth: 0, flex: { xs: '1 1 100%', sm: '1 1 180px' } }}>
                    <Typography variant="body2" color="text.secondary" sx={textWrapSx}>
                        {translate('resources.roles.fields.updatedAt')}
                    </Typography>
                    <DateField 
                        source="updatedAt" 
                        locales="en-US"
                        options={{ 
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        }}
                    />
                </Box>
                <Box sx={{ minWidth: 0, flex: { xs: '1 1 100%', sm: '1 1 220px' } }}>
                    <Typography variant="body2" color="text.secondary" sx={textWrapSx}>
                        {translate('resources.roles.fields.userCount')}
                    </Typography>
                    <Chip
                        icon={<People />}
                        label={`${userCount} ${translate('resources.roles.list.users', { smart_count: userCount })}`}
                        size="small"
                        sx={{
                            mt: 0.5,
                            backgroundColor: userCount > 0 ? '#E3F2FD' : '#F5F5F5',
                            color: userCount > 0 ? '#1976D2' : '#666666',
                            ...wrappedChipSx,
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

const PermissionManager = () => {
    const translate = useTranslate();
    const record = useRecordContext();
    const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
    
    // Get translated permission groups
    const PERMISSION_GROUPS = getPermissionGroups(translate);
    
    // Use React Admin's form input system
    const { field } = useInput({ 
        source: 'permissions',
        defaultValue: []
    });
    
    // Initialize permissions from record
    useEffect(() => {
        if (record && record.permissions && field.value.length === 0) {
            const permissions = Array.isArray(record.permissions) 
                ? record.permissions.map((p: any) => typeof p === 'string' ? p : p.permission)
                : [];
            field.onChange(permissions);
        }
    }, [record, field]);
    
    const selectedPermissions = field.value || [];
    
    const handlePermissionToggle = (permissionId: string) => {
        const newPermissions = selectedPermissions.includes(permissionId)
            ? selectedPermissions.filter((id: string) => id !== permissionId)
            : [...selectedPermissions, permissionId];
        field.onChange(newPermissions);
    };
    
    const handleGroupToggle = (groupKey: string) => {
        setExpandedGroups(prev =>
            prev.includes(groupKey)
                ? prev.filter(key => key !== groupKey)
                : [...prev, groupKey]
        );
    };
    
    const handleSelectAllInGroup = (groupKey: string) => {
        const group = PERMISSION_GROUPS[groupKey as keyof typeof PERMISSION_GROUPS];
        const groupPermissionIds = group.permissions.map(p => p.id);
        const allSelected = groupPermissionIds.every(id => selectedPermissions.includes(id));
        
        if (allSelected) {
            field.onChange(selectedPermissions.filter((id: string) => !groupPermissionIds.includes(id)));
        } else {
            field.onChange(Array.from(new Set([...selectedPermissions, ...groupPermissionIds])));
        }
    };
    
    return (
        <>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, mb: { xs: 2, sm: 3 }, flexWrap: 'wrap', minWidth: 0 }}>
                <Security sx={{ color: '#009A75', flexShrink: 0 }} />
                <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, ...textWrapSx, minWidth: 0 }}>
                    {translate('resources.roles.sections.permissions')}
                </Typography>
                <Chip
                    label={`${selectedPermissions.length} ${translate('resources.roles.selected')}`}
                    size="small"
                    sx={{
                        backgroundColor: '#009A7515',
                        color: '#009A75',
                        fontWeight: 500,
                        ...wrappedChipSx,
                    }}
                />
            </Box>

            <Alert severity="warning" sx={{ mb: { xs: 2, sm: 3 } }}>
                <Typography variant="body2" sx={textWrapSx}>
                    {translate('resources.roles.permissions_warning')}
                </Typography>
            </Alert>
            
            {Object.entries(PERMISSION_GROUPS).map(([groupKey, group]) => {
                const groupPermissionIds = group.permissions.map(p => p.id);
                const selectedCount = groupPermissionIds.filter(id => selectedPermissions.includes(id)).length;
                const allSelected = selectedCount === group.permissions.length;
                const someSelected = selectedCount > 0 && selectedCount < group.permissions.length;
                
                return (
                    <Accordion
                        key={groupKey}
                        expanded={expandedGroups.includes(groupKey)}
                        onChange={() => handleGroupToggle(groupKey)}
                        sx={{
                            mb: 1,
                            '&:before': { display: 'none' },
                            boxShadow: 'none',
                            border: '1px solid #E0E0E0',
                            '&.Mui-expanded': {
                                margin: '0 0 8px 0',
                            },
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore />}
                            sx={{
                                '&.Mui-expanded': {
                                    minHeight: 48,
                                },
                                '& .MuiAccordionSummary-content': {
                                    my: 1,
                                    width: '100%',
                                    minWidth: 0,
                                },
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 1, sm: 2 }, width: '100%', minWidth: 0, flexWrap: 'wrap' }}>
                                <Box sx={{ color: group.color, flexShrink: 0 }}>
                                    {group.icon}
                                </Box>
                                <Typography sx={{ flexGrow: 1, minWidth: 0, ...textWrapSx }}>
                                    {group.label}
                                </Typography>
                                {selectedCount > 0 && (
                                    <Chip
                                        label={`${selectedCount}/${group.permissions.length}`}
                                        size="small"
                                        sx={{
                                            backgroundColor: group.color + '20',
                                            color: group.color,
                                            fontWeight: 500,
                                            mr: { xs: 0, sm: 1 },
                                            ...wrappedChipSx,
                                        }}
                                    />
                                )}
                                <Checkbox
                                    checked={allSelected}
                                    indeterminate={someSelected}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSelectAllInGroup(groupKey);
                                    }}
                                    sx={{
                                        color: '#E0E0E0',
                                        flexShrink: 0,
                                        '&.Mui-checked': {
                                            color: '#009A75',
                                        },
                                    }}
                                />
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: { xs: 1.5, sm: 2 }, minWidth: 0 }}>
                            <Divider sx={{ mb: 2 }} />
                            {group.permissions.map((permission) => (
                                <FormControlLabel
                                    key={permission.id}
                                    control={
                                        <Checkbox
                                            checked={selectedPermissions.includes(permission.id)}
                                            onChange={() => handlePermissionToggle(permission.id)}
                                            sx={{
                                                color: '#E0E0E0',
                                                mt: 0.25,
                                                '&.Mui-checked': {
                                                    color: group.color,
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="body1" sx={textWrapSx}>
                                                {permission.label}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={textWrapSx}>
                                                {permission.description}
                                            </Typography>
                                        </Box>
                                    }
                                    sx={{
                                        mb: 2,
                                        ml: 0,
                                        mr: 0,
                                        width: '100%',
                                        minWidth: 0,
                                        alignItems: 'flex-start',
                                        '& .MuiFormControlLabel-label': {
                                            width: '100%',
                                            minWidth: 0,
                                        },
                                    }}
                                />
                            ))}
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </>
    );
};

const PageHeader = () => {
    const redirect = useRedirect();
    const record = useRecordContext();
    const translate = useTranslate();

    return (
        <Box sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexWrap: 'wrap',
            gap: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 4 },
            minWidth: 0,
        }}>
            <IconButton
                onClick={() => redirect('/roles')}
                size="large"
                sx={{
                    flexShrink: 0,
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('ra.action.back')}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' }, ...textWrapSx }}>
                    {translate('resources.roles.edit.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={textWrapSx}>
                    {record?.name ? `${translate('resources.roles.edit.subtitle')}: ${record.name}` : translate('resources.roles.edit.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};

const RoleEdit = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({});

    return (
        <Edit
            title={false}
            component="div"
            redirect="list"
            mutationMode="pessimistic"
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                    notify('ra.notification.updated', { type: 'success', messageArgs: { smart_count: 1 } });
                    redirect('list', 'roles');
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapRoleBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(roleFieldLabelKey(field), { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                },
            }}
            sx={{
                maxWidth: '100%',
                backgroundColor: 'transparent',
                '& .RaEdit-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    maxWidth: '100%',
                },
            }}
        >
            <SimpleForm
                toolbar={<EditToolbar />}
                sx={{
                    maxWidth: '100%',
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', minWidth: 0, pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: { xs: 2, sm: 3 } }}>
                                <Info sx={{ color: '#009A75' }} />
                                <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, ...textWrapSx }}>
                                    {translate('resources.roles.sections.role_info')}
                                </Typography>
                            </Box>

                            <RoleInfo />
                        </CardContent>
                    </Card>
                    
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: { xs: 2, sm: 3 } }}>
                                <Description sx={{ color: '#009A75' }} />
                                <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, ...textWrapSx }}>
                                    {translate('resources.roles.sections.basic_info')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <TextInput
                                    source="name"
                                    label={translate('resources.roles.fields.name')}
                                    validate={[
                                        required(),
                                        minLength(3),
                                        regex(/^[a-zA-Z0-9\s-]+$/, translate('resources.roles.validation.name_format')),
                                    ]}
                                    fullWidth
                                    helperText={translate('resources.roles.helpers.name')}
                                />
                                
                                <TextInput
                                    source="description"
                                    label={translate('resources.roles.fields.description')}
                                    multiline
                                    rows={3}
                                    fullWidth
                                    helperText={translate('resources.roles.helpers.description')}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                    
                    <Card sx={{ overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <PermissionManager />
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>
        </Edit>
    );
};

export default RoleEdit;
